/**
 * Which language the app shows — English unless a user deliberately chose
 * otherwise.
 *
 * Users (chairmen and admins among them) were seeing their dashboard in Hindi
 * without ever asking for it. i18next's browser detector read the phone's
 * language (`navigator.language` = "hi-IN") on first launch and then CACHED
 * it in `campushub_lang`, so from then on the auto-detected guess looked like
 * a saved choice. Admin-tier roles have no language switcher at all (by
 * design — they work in English), so they had no way back.
 *
 * Now:
 *   - the device language is never consulted; the default is English;
 *   - only an explicit pick in the switcher / Settings → Language is stored,
 *     under a NEW key, so every value under the old key (which may be a
 *     detector guess) is ignored — a user who really wants Hindi picks it
 *     again once;
 *   - roles without a language switcher are always English, whatever is
 *     stored on a shared device.
 */

export const SUPPORTED_LANGUAGES = {
  en: "English",
  hi: "हिन्दी",
  te: "తెలుగు",
  // Future: ta, kn, bn, mr
} as const;

export type SupportedLanguage = keyof typeof SUPPORTED_LANGUAGES;

export const DEFAULT_LANGUAGE: SupportedLanguage = "en";

/** Written only when a person picks a language. Never by detection. */
export const LANGUAGE_CHOICE_KEY = "campushub_lang_choice";

/** The roles offered a language picker; every other role is English. */
export const LANGUAGE_CHOICE_ROLES: ReadonlySet<string> = new Set(["PARENT", "STUDENT", "TEACHER", "DRIVER"]);

export function isSupportedLanguage(v: unknown): v is SupportedLanguage {
  return typeof v === "string" && Object.prototype.hasOwnProperty.call(SUPPORTED_LANGUAGES, v);
}

type StorageLike = Pick<Storage, "getItem" | "setItem">;

function defaultStorage(): StorageLike | null {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
}

/** The language this person explicitly chose, or null if they never did. */
export function readLanguageChoice(storage: StorageLike | null = defaultStorage()): SupportedLanguage | null {
  try {
    const v = storage?.getItem(LANGUAGE_CHOICE_KEY);
    return isSupportedLanguage(v) ? v : null;
  } catch {
    return null;
  }
}

export function saveLanguageChoice(lang: SupportedLanguage, storage: StorageLike | null = defaultStorage()): void {
  try {
    storage?.setItem(LANGUAGE_CHOICE_KEY, lang);
  } catch {
    /* private mode / blocked storage — the choice lasts this session only */
  }
}

/**
 * The language to show. Signed out: the person's own choice on this device,
 * else English. Signed in: English for every role without a picker; the
 * stored choice (else English) for the roles that have one.
 */
export function languageFor(role: string | null | undefined, choice: SupportedLanguage | null): SupportedLanguage {
  if (role && !LANGUAGE_CHOICE_ROLES.has(role)) return DEFAULT_LANGUAGE;
  return choice ?? DEFAULT_LANGUAGE;
}
